import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, PlayCircle, Building2, MessageSquare, X } from "lucide-react";

const FLAG_KEY = "eco_show_welcome";
const seenKey = (uid) => `eco_onboarded_${uid}`;

const tiles = [
  { icon: Sparkles, label: "Proposals", desc: "Draft an idea. Our AI industrial-designer refines materials, costs and mechanisms.", to: "/proposals" },
  { icon: PlayCircle, label: "Learn", desc: "Short and long-form videos from scientists and in-field creators.", to: "/learn" },
  { icon: Building2, label: "Companies", desc: "Answer live ecological bounties from real companies. Get paid for solutions.", to: "/companies" },
  { icon: MessageSquare, label: "Rooms", desc: "Topic-scoped chat rooms across every branch of eco-engineering.", to: "/rooms" },
];

export default function WelcomeOnboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const flag = localStorage.getItem(FLAG_KEY);
    const seen = localStorage.getItem(seenKey(user.id));
    if (flag === "1" && !seen) setOpen(true);
  }, [user]);

  const close = (goTo) => {
    if (user) localStorage.setItem(seenKey(user.id), "1");
    localStorage.removeItem(FLAG_KEY);
    setOpen(false);
    if (goTo) nav(goTo);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close(null)}>
      <DialogContent
        data-testid="welcome-onboarding"
        className="max-w-2xl rounded-none border-primary/40 p-0 gap-0"
      >
        <div className="relative border-b border-border bg-primary/5 p-6">
          <DialogTitle className="sr-only">Welcome to Eco Chamber</DialogTitle>
          <button
            data-testid="welcome-close"
            onClick={() => close(null)}
            className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="mono text-xs text-primary">// welcome, {user.display_name?.split(" ")[0] || "friend"}</div>
          <h2 className="mt-2 text-3xl font-black tracking-tighter">You're in the Eco Chamber.</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Four ways to get started — pick where you want to begin.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.to}
                data-testid={`welcome-tile-${t.label.toLowerCase()}`}
                onClick={() => close(t.to)}
                className="group flex flex-col items-start gap-3 bg-background p-5 text-left transition-colors hover:bg-primary/5"
              >
                <div className="flex h-10 w-10 items-center justify-center border border-primary/40 text-primary transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-bold tracking-tight">{t.label}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{t.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-background p-4">
          <button
            data-testid="welcome-skip"
            onClick={() => close(null)}
            className="mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Skip for now
          </button>
          <Button
            data-testid="welcome-start"
            onClick={() => close("/proposals")}
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Start with Proposals
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
